import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import React, { createRef } from 'react';
import CopyToClipboardButton from './CopyToClipboardButton.js';
import EditableFieldWithWarnings from './EditableFieldWithWarnings.js';
import { buildArraySchemaWarningPath } from './PathUtils.js';

// Helper functions for building JSONPath expressions for incident details (new array schema)
const buildIncidentFieldPaths = (documentKey, itemIndex, fieldName) => {
    const base = `$.documents['${documentKey}'].analysis.incident_details[${itemIndex}]`;
    return {
        textPath: `${base}.${fieldName}`,
        needsReviewPath: buildArraySchemaWarningPath(base, fieldName, 'needs_review'),
        caveatsPath: buildArraySchemaWarningPath(base, fieldName, 'caveats')
    };
};

const buildLocationFieldPaths = (documentKey, itemIndex, fieldName) => {
    const base = `$.documents['${documentKey}'].analysis.incident_details[${itemIndex}].location`;
    return {
        textPath: `${base}.${fieldName}`,
        needsReviewPath: buildArraySchemaWarningPath(base, fieldName, 'needs_review'),
        caveatsPath: buildArraySchemaWarningPath(base, fieldName, 'caveats')
    };
};

const buildPartyFieldPaths = (documentKey, itemIndex, partyIndex, fieldName) => {
    const base = `$.documents['${documentKey}'].analysis.incident_details[${itemIndex}].parties[${partyIndex}]`;
    return {
        textPath: `${base}.${fieldName}`,
        needsReviewPath: buildArraySchemaWarningPath(base, fieldName, 'needs_review'),
        caveatsPath: buildArraySchemaWarningPath(base, fieldName, 'caveats')
    };
};

const buildVehicleFieldPaths = (documentKey, itemIndex, partyIndex, fieldName) => {
    const base = `$.documents['${documentKey}'].analysis.incident_details[${itemIndex}].parties[${partyIndex}].vehicle`;
    return {
        textPath: `${base}.${fieldName}`,
        needsReviewPath: buildArraySchemaWarningPath(base, fieldName, 'needs_review'),
        caveatsPath: buildArraySchemaWarningPath(base, fieldName, 'caveats')
    };
};

const buildInsuranceFieldPaths = (documentKey, itemIndex, partyIndex, fieldName) => {
    const base = `$.documents['${documentKey}'].analysis.incident_details[${itemIndex}].parties[${partyIndex}].vehicle.insurance_details`;
    return {
        textPath: `${base}.${fieldName}`,
        needsReviewPath: buildArraySchemaWarningPath(base, fieldName, 'needs_review'),
        caveatsPath: buildArraySchemaWarningPath(base, fieldName, 'caveats')
    };
};

const buildWitnessFieldPaths = (documentKey, itemIndex, witnessIndex, fieldName) => {
    const base = `$.documents['${documentKey}'].analysis.incident_details[${itemIndex}].witnesses[${witnessIndex}]`;
    return {
        textPath: `${base}.${fieldName}`,
        needsReviewPath: buildArraySchemaWarningPath(base, fieldName, 'needs_review'),
        caveatsPath: buildArraySchemaWarningPath(base, fieldName, 'caveats')
    };
};

export default function IncidentDetails({ caseInfo, setCaseInfo, fileNamesToFilePropertiesMap, displayedDocument, setDisplayedDocument, setSnackBarMessage }) {
    const tableRef = createRef();
    var documentKeysToDisplay = caseInfo !== null && 'documents' in caseInfo ?
        Object.keys(caseInfo['documents']).filter(docKey => fileNamesToFilePropertiesMap[caseInfo['documents'][docKey]['filename']]['selected']) :
        [];
    const isIncidentDetailsExtractionInProgress = caseInfo && caseInfo['documents'] && Object.values(caseInfo['documents']).some(doc => doc['analysis_queue']?.includes('categorize') || doc['analysis_queue']?.includes('collision_report') || doc['analysis_queue']?.includes('personal_injury_catchall'));

    var incidentDetailsItems = documentKeysToDisplay.map(documentKey => {
        caseInfo['documents'][documentKey]['analysis']?.['incident_details']?.forEach((incidentDetailsItem, i) => {
            incidentDetailsItem['id'] = documentKey + i; // Used in UI as keys for use by React framework
            incidentDetailsItem['source_filekey'] = documentKey; // File used by SourceDocumentViewer
            incidentDetailsItem['itemIndex'] = i; // Store the index for path building
        });
        return caseInfo['documents'][documentKey]['analysis']?.['incident_details'];
    })
        .filter(incidentDetailsArr => incidentDetailsArr) // the ?. above can cause null entries; filter them
        .reduce((accumulator, incidentDetailsArr) => accumulator.concat(incidentDetailsArr), [])
        .filter(incidentDetailsItem => incidentDetailsItem.date && incidentDetailsItem.date.length > 0);

    if (incidentDetailsItems.length === 0) {
        if (isIncidentDetailsExtractionInProgress) {
            return (<Box sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2
            }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    {isIncidentDetailsExtractionInProgress && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <img
                                src="/loading.gif"
                                alt="loading..."
                                title="Processing..."
                                style={{
                                    marginRight: "8px",
                                    height: "16px",
                                    verticalAlign: "middle"
                                }}
                            />
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'var(--text-primary)',
                                    fontStyle: 'italic',
                                    fontSize: '1.1rem',
                                    fontWeight: 500
                                }}
                            >
                                Incident details are being extracted...
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>
            )
        }

        return (
            <Box sx={{
                p: 3,
                textAlign: 'center',
                mt: 8,
                mb: 8
            }}>
                <Typography
                    variant="h6"
                    sx={{
                        color: 'var(--text-secondary)',
                        fontWeight: 400,
                        fontSize: '1.1rem',
                        lineHeight: 1.5
                    }}
                >
                    Incident details will be automatically extracted from uploaded documents such as collision reports and displayed here.
                </Typography>
            </Box>
        )
    }

    var currentPartyNumber = 1;
    var currentWitnessNumber = 1;
    var tableBodyRows = incidentDetailsItems
        .sort((item1, item2) =>
            Date.parse(item1.date) -
            Date.parse(item2.date))
        .map(incidentDetailsItem => {
            const documentKey = incidentDetailsItem.source_filekey;
            const docInfo = caseInfo.documents[documentKey];
            const filename = docInfo?.['filename'];
            const itemIndex = incidentDetailsItem.itemIndex;

            // Rows: Date, Date:	time, Location:	Description, Parties: Name, Dob, Role, statements
            return (
                <React.Fragment key={incidentDetailsItem['source_filekey'] + ':incidentDetails'}>
                    <TableRow
                        key={incidentDetailsItem['source_filekey'] + ':incidentDate'}
                        className={incidentDetailsItem['source_filekey'] === displayedDocument?.['key'] ? "selected" : ""}
                        sx={{
                            '&:hover .source-view-icon': {
                                visibility: 'visible',
                                opacity: 0.7
                            }
                        }}>
                        <TableCell sx={{ fontWeight: 500 }}> Incident Date </TableCell>
                        <TableCell colSpan={4}>
                            <EditableFieldWithWarnings
                                onWarningClick={(e) => {
                                    e.stopPropagation();
                                    setDisplayedDocument({
                                        'key': documentKey,
                                        'page': 1
                                    });
                                }}
                                caseInfo={caseInfo}
                                setCaseInfo={setCaseInfo}
                                {...buildIncidentFieldPaths(documentKey, itemIndex, 'date')}
                                placeholder="Date not available"
                                setSnackBarMessage={setSnackBarMessage}
                            />
                        </TableCell>
                        <TableCell>
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography
                                    component="span"
                                    title="Click to view source document"
                                    sx={{ cursor: 'pointer', textDecoration: 'underline', fontSize: '0.9rem' }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setDisplayedDocument({ 'key': incidentDetailsItem['source_filekey'], 'page': 1 });
                                    }}
                                >
                                    {filename}
                                </Typography>
                                <IconButton
                                    className="source-view-icon"
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setDisplayedDocument({ 'key': incidentDetailsItem['source_filekey'], 'page': 1 });
                                    }}
                                    sx={{
                                        padding: '2px',
                                        visibility: 'hidden',
                                        opacity: 0,
                                        transition: 'opacity 0.2s, visibility 0.2s',
                                        '&:hover': {
                                            opacity: 1,
                                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                        }
                                    }}
                                    title="Click to view source document"
                                >
                                    <VisibilityIcon />
                                </IconButton>
                            </Box>
                        </TableCell>
                    </TableRow>
                    <TableRow
                        key={incidentDetailsItem['source_filekey'] + ':incidentTime'}
                        className={incidentDetailsItem['source_filekey'] === displayedDocument?.['key'] ? "selected" : ""}
                        sx={{
                            '&:hover .source-view-icon': {
                                visibility: 'visible',
                                opacity: 0.7
                            }
                        }}>
                        <TableCell sx={{ fontWeight: 500 }}> Incident Time </TableCell>
                        <TableCell colSpan={4}>
                            <EditableFieldWithWarnings
                                onWarningClick={(e) => {
                                    e.stopPropagation();
                                    setDisplayedDocument({
                                        'key': documentKey,
                                        'page': 1
                                    });
                                }}
                                caseInfo={caseInfo}
                                setCaseInfo={setCaseInfo}
                                {...buildIncidentFieldPaths(documentKey, itemIndex, 'time')}
                                placeholder="Time not available"
                                setSnackBarMessage={setSnackBarMessage}
                            />
                        </TableCell>
                        <TableCell>
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography
                                    component="span"
                                    title="Click to view source document"
                                    sx={{ cursor: 'pointer', textDecoration: 'underline', fontSize: '0.9rem' }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setDisplayedDocument({ 'key': incidentDetailsItem['source_filekey'], 'page': 1 });
                                    }}
                                >
                                    {filename}
                                </Typography>
                                <IconButton
                                    className="source-view-icon"
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setDisplayedDocument({ 'key': incidentDetailsItem['source_filekey'], 'page': 1 });
                                    }}
                                    sx={{
                                        padding: '2px',
                                        visibility: 'hidden',
                                        opacity: 0,
                                        transition: 'opacity 0.2s, visibility 0.2s',
                                        '&:hover': {
                                            opacity: 1,
                                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                        }
                                    }}
                                    title="Click to view source document"
                                >
                                    <VisibilityIcon />
                                </IconButton>
                            </Box>
                        </TableCell>
                    </TableRow>
                    <TableRow
                        key={incidentDetailsItem['source_filekey'] + ':incidentLocation'}
                        className={incidentDetailsItem['source_filekey'] === displayedDocument?.['key'] ? "selected" : ""}
                        sx={{
                            '&:hover .source-view-icon': {
                                visibility: 'visible',
                                opacity: 0.7
                            }
                        }}>
                        <TableCell sx={{ fontWeight: 500 }}> Incident Location </TableCell>
                        <TableCell sx={{ fontWeight: 500 }}> Address </TableCell>
                        <TableCell colSpan={3}>
                            <EditableFieldWithWarnings
                                onWarningClick={(e) => {
                                    e.stopPropagation();
                                    setDisplayedDocument({
                                        'key': documentKey,
                                        'page': 1
                                    });
                                }}
                                caseInfo={caseInfo}
                                setCaseInfo={setCaseInfo}
                                {...buildLocationFieldPaths(documentKey, itemIndex, 'address')}
                                placeholder="Street address not available"
                                setSnackBarMessage={setSnackBarMessage}
                            />
                        </TableCell>
                        <TableCell>
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography
                                    component="span"
                                    title="Click to view source document"
                                    sx={{ cursor: 'pointer', textDecoration: 'underline', fontSize: '0.9rem' }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setDisplayedDocument({ 'key': incidentDetailsItem['source_filekey'], 'page': 1 });
                                    }}
                                >
                                    {filename}
                                </Typography>
                                <IconButton
                                    className="source-view-icon"
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setDisplayedDocument({ 'key': incidentDetailsItem['source_filekey'], 'page': 1 });
                                    }}
                                    sx={{
                                        padding: '2px',
                                        visibility: 'hidden',
                                        opacity: 0,
                                        transition: 'opacity 0.2s, visibility 0.2s',
                                        '&:hover': {
                                            opacity: 1,
                                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                        }
                                    }}
                                    title="Click to view source document"
                                >
                                    <VisibilityIcon />
                                </IconButton>
                            </Box>
                        </TableCell>
                    </TableRow>
                    <TableRow
                        key={incidentDetailsItem['source_filekey'] + ':incidentCity'}
                        className={incidentDetailsItem['source_filekey'] === displayedDocument?.['key'] ? "selected" : ""}
                        sx={{
                            '&:hover .source-view-icon': {
                                visibility: 'visible',
                                opacity: 0.7
                            }
                        }}>
                        <TableCell></TableCell>
                        <TableCell sx={{ fontWeight: 500 }}> City </TableCell>
                        <TableCell colSpan={3}>
                            <EditableFieldWithWarnings
                                onWarningClick={(e) => {
                                    e.stopPropagation();
                                    setDisplayedDocument({
                                        'key': documentKey,
                                        'page': 1
                                    });
                                }}
                                caseInfo={caseInfo}
                                setCaseInfo={setCaseInfo}
                                {...buildLocationFieldPaths(documentKey, itemIndex, 'city')}
                                placeholder="City not available"
                                setSnackBarMessage={setSnackBarMessage}
                            />
                        </TableCell>
                        <TableCell>
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography
                                    component="span"
                                    title="Click to view source document"
                                    sx={{ cursor: 'pointer', textDecoration: 'underline', fontSize: '0.9rem' }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setDisplayedDocument({ 'key': incidentDetailsItem['source_filekey'], 'page': 1 });
                                    }}
                                >
                                    {filename}
                                </Typography>
                                <IconButton
                                    className="source-view-icon"
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setDisplayedDocument({ 'key': incidentDetailsItem['source_filekey'], 'page': 1 });
                                    }}
                                    sx={{
                                        padding: '2px',
                                        visibility: 'hidden',
                                        opacity: 0,
                                        transition: 'opacity 0.2s, visibility 0.2s',
                                        '&:hover': {
                                            opacity: 1,
                                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                        }
                                    }}
                                    title="Click to view source document"
                                >
                                    <VisibilityIcon />
                                </IconButton>
                            </Box>
                        </TableCell>
                    </TableRow>

                    {incidentDetailsItem.weather && <TableRow
                        key={incidentDetailsItem['source_filekey']}
                        className={incidentDetailsItem['source_filekey'] === displayedDocument?.['key'] ? "selected" : ""}
                        sx={{
                            '&:hover .source-view-icon': {
                                visibility: 'visible',
                                opacity: 0.7
                            }
                        }}>
                        <TableCell sx={{ fontWeight: 500 }}> Weather </TableCell>
                        <TableCell colSpan={4}>
                            <EditableFieldWithWarnings
                                onWarningClick={(e) => {
                                    e.stopPropagation();
                                    setDisplayedDocument({
                                        'key': documentKey,
                                        'page': 1
                                    });
                                }}
                                caseInfo={caseInfo}
                                setCaseInfo={setCaseInfo}
                                {...buildIncidentFieldPaths(documentKey, itemIndex, 'weather')}
                                setSnackBarMessage={setSnackBarMessage}
                            />
                        </TableCell>
                        <TableCell>
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography
                                    component="span"
                                    title="Click to view source document"
                                    sx={{ cursor: 'pointer', textDecoration: 'underline', fontSize: '0.9rem' }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setDisplayedDocument({ 'key': incidentDetailsItem['source_filekey'], 'page': 1 });
                                    }}
                                >
                                    {filename}
                                </Typography>
                                <IconButton
                                    className="source-view-icon"
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setDisplayedDocument({ 'key': incidentDetailsItem['source_filekey'], 'page': 1 });
                                    }}
                                    sx={{
                                        padding: '2px',
                                        visibility: 'hidden',
                                        opacity: 0,
                                        transition: 'opacity 0.2s, visibility 0.2s',
                                        '&:hover': {
                                            opacity: 1,
                                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                        }
                                    }}
                                    title="Click to view source document"
                                >
                                    <VisibilityIcon />
                                </IconButton>
                            </Box>
                        </TableCell>
                    </TableRow>}

                    {incidentDetailsItem.road_conditions && <TableRow
                        key={incidentDetailsItem['source_filekey'] + ':incidentRoadConditions'}
                        className={incidentDetailsItem['source_filekey'] === displayedDocument?.['key'] ? "selected" : ""}
                        sx={{
                            '&:hover .source-view-icon': {
                                visibility: 'visible',
                                opacity: 0.7
                            }
                        }}>
                        <TableCell sx={{ fontWeight: 500 }}> Road Conditions </TableCell>
                        <TableCell colSpan={4}>
                            <EditableFieldWithWarnings
                                onWarningClick={(e) => {
                                    e.stopPropagation();
                                    setDisplayedDocument({
                                        'key': documentKey,
                                        'page': 1
                                    });
                                }}
                                caseInfo={caseInfo}
                                setCaseInfo={setCaseInfo}
                                {...buildIncidentFieldPaths(documentKey, itemIndex, 'road_conditions')}
                                setSnackBarMessage={setSnackBarMessage}
                            />
                        </TableCell>
                        <TableCell>
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography
                                    component="span"
                                    title="Click to view source document"
                                    sx={{ cursor: 'pointer', textDecoration: 'underline', fontSize: '0.9rem' }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setDisplayedDocument({ 'key': incidentDetailsItem['source_filekey'], 'page': 1 });
                                    }}
                                >
                                    {filename}
                                </Typography>
                                <IconButton
                                    className="source-view-icon"
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setDisplayedDocument({ 'key': incidentDetailsItem['source_filekey'], 'page': 1 });
                                    }}
                                    sx={{
                                        padding: '2px',
                                        visibility: 'hidden',
                                        opacity: 0,
                                        transition: 'opacity 0.2s, visibility 0.2s',
                                        '&:hover': {
                                            opacity: 1,
                                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                        }
                                    }}
                                    title="Click to view source document"
                                >
                                    <VisibilityIcon />
                                </IconButton>
                            </Box>
                        </TableCell>
                    </TableRow>}

                    {incidentDetailsItem.other_conditions && <TableRow
                        key={incidentDetailsItem['source_filekey'] + ':incidentOtherConditions'}
                        className={incidentDetailsItem['source_filekey'] === displayedDocument?.['key'] ? "selected" : ""}
                        sx={{
                            '&:hover .source-view-icon': {
                                visibility: 'visible',
                                opacity: 0.7
                            }
                        }}>
                        <TableCell sx={{ fontWeight: 500 }}> Other Conditions </TableCell>
                        <TableCell colSpan={4}>
                            <EditableFieldWithWarnings
                                onWarningClick={(e) => {
                                    e.stopPropagation();
                                    setDisplayedDocument({
                                        'key': documentKey,
                                        'page': 1
                                    });
                                }}
                                caseInfo={caseInfo}
                                setCaseInfo={setCaseInfo}
                                {...buildIncidentFieldPaths(documentKey, itemIndex, 'other_conditions')}
                                setSnackBarMessage={setSnackBarMessage}
                            />
                        </TableCell>
                        <TableCell>
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography
                                    component="span"
                                    title="Click to view source document"
                                    sx={{ cursor: 'pointer', textDecoration: 'underline', fontSize: '0.9rem' }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setDisplayedDocument({ 'key': incidentDetailsItem['source_filekey'], 'page': 1 });
                                    }}
                                >
                                    {filename}
                                </Typography>
                                <IconButton
                                    className="source-view-icon"
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setDisplayedDocument({ 'key': incidentDetailsItem['source_filekey'], 'page': 1 });
                                    }}
                                    sx={{
                                        padding: '2px',
                                        visibility: 'hidden',
                                        opacity: 0,
                                        transition: 'opacity 0.2s, visibility 0.2s',
                                        '&:hover': {
                                            opacity: 1,
                                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                        }
                                    }}
                                    title="Click to view source document"
                                >
                                    <VisibilityIcon />
                                </IconButton>
                            </Box>
                        </TableCell>
                    </TableRow>}

                    {incidentDetailsItem.incident_type && <TableRow
                        key={incidentDetailsItem['source_filekey'] + ':incidentIncidentType'}
                        className={incidentDetailsItem['source_filekey'] === displayedDocument?.['key'] ? "selected" : ""}
                        sx={{
                            '&:hover .source-view-icon': {
                                visibility: 'visible',
                                opacity: 0.7
                            }
                        }}>
                        <TableCell sx={{ fontWeight: 500 }}> Incident Type </TableCell>
                        <TableCell colSpan={4}>
                            <EditableFieldWithWarnings
                                onWarningClick={(e) => {
                                    e.stopPropagation();
                                    setDisplayedDocument({
                                        'key': documentKey,
                                        'page': 1
                                    });
                                }}
                                caseInfo={caseInfo}
                                setCaseInfo={setCaseInfo}
                                {...buildIncidentFieldPaths(documentKey, itemIndex, 'incident_type')}
                                setSnackBarMessage={setSnackBarMessage}
                            />
                        </TableCell>
                        <TableCell>
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography
                                    component="span"
                                    title="Click to view source document"
                                    sx={{ cursor: 'pointer', textDecoration: 'underline', fontSize: '0.9rem' }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setDisplayedDocument({ 'key': incidentDetailsItem['source_filekey'], 'page': 1 });
                                    }}
                                >
                                    {filename}
                                </Typography>
                                <IconButton
                                    className="source-view-icon"
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setDisplayedDocument({ 'key': incidentDetailsItem['source_filekey'], 'page': 1 });
                                    }}
                                    sx={{
                                        padding: '2px',
                                        visibility: 'hidden',
                                        opacity: 0,
                                        transition: 'opacity 0.2s, visibility 0.2s',
                                        '&:hover': {
                                            opacity: 1,
                                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                        }
                                    }}
                                    title="Click to view source document"
                                >
                                    <VisibilityIcon />
                                </IconButton>
                            </Box>
                        </TableCell>
                    </TableRow>}

                    {incidentDetailsItem.incident_description && <TableRow
                        key={incidentDetailsItem['source_filekey'] + ':incidentIncidentDescription'}
                        className={incidentDetailsItem['source_filekey'] === displayedDocument?.['key'] ? "selected" : ""}
                        sx={{
                            '&:hover .source-view-icon': {
                                visibility: 'visible',
                                opacity: 0.7
                            }
                        }}>
                        <TableCell sx={{ fontWeight: 500 }}> Incident Description </TableCell>
                        <TableCell colSpan={4}>
                            <EditableFieldWithWarnings
                                onWarningClick={(e) => {
                                    e.stopPropagation();
                                    setDisplayedDocument({
                                        'key': documentKey,
                                        'page': 1
                                    });
                                }}
                                caseInfo={caseInfo}
                                setCaseInfo={setCaseInfo}
                                {...buildIncidentFieldPaths(documentKey, itemIndex, 'incident_description')}
                                setSnackBarMessage={setSnackBarMessage}
                            />
                        </TableCell>
                        <TableCell>
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography
                                    component="span"
                                    title="Click to view source document"
                                    sx={{ cursor: 'pointer', textDecoration: 'underline', fontSize: '0.9rem' }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setDisplayedDocument({ 'key': incidentDetailsItem['source_filekey'], 'page': 1 });
                                    }}
                                >
                                    {filename}
                                </Typography>
                                <IconButton
                                    className="source-view-icon"
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setDisplayedDocument({ 'key': incidentDetailsItem['source_filekey'], 'page': 1 });
                                    }}
                                    sx={{
                                        padding: '2px',
                                        visibility: 'hidden',
                                        opacity: 0,
                                        transition: 'opacity 0.2s, visibility 0.2s',
                                        '&:hover': {
                                            opacity: 1,
                                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                        }
                                    }}
                                    title="Click to view source document"
                                >
                                    <VisibilityIcon />
                                </IconButton>
                            </Box>
                        </TableCell>
                    </TableRow>}

                    {incidentDetailsItem.parties && incidentDetailsItem.parties.length > 0 && <TableRow
                        key={incidentDetailsItem['source_filekey'] + ':incidentParties'}
                        className={incidentDetailsItem['source_filekey'] === displayedDocument?.['key'] ? "selected" : ""}
                        sx={{
                            '&:hover .source-view-icon': {
                                visibility: 'visible',
                                opacity: 0.7
                            }
                        }}>
                        <TableCell colSpan={5} sx={{ fontWeight: 500 }}> Parties </TableCell>
                        <TableCell>
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography
                                    component="span"
                                    title="Click to view source document"
                                    sx={{ cursor: 'pointer', textDecoration: 'underline', fontSize: '0.9rem' }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setDisplayedDocument({ 'key': incidentDetailsItem['source_filekey'], 'page': 1 });
                                    }}
                                >
                                    {filename}
                                </Typography>
                                <IconButton
                                    className="source-view-icon"
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setDisplayedDocument({ 'key': incidentDetailsItem['source_filekey'], 'page': 1 });
                                    }}
                                    sx={{
                                        padding: '2px',
                                        visibility: 'hidden',
                                        opacity: 0,
                                        transition: 'opacity 0.2s, visibility 0.2s',
                                        '&:hover': {
                                            opacity: 1,
                                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                        }
                                    }}
                                    title="Click to view source document"
                                >
                                    <VisibilityIcon />
                                </IconButton>
                            </Box>
                        </TableCell>
                    </TableRow>}

                    {incidentDetailsItem.parties && incidentDetailsItem.parties.map((party, partyIndex) => {
                        return (
                            <React.Fragment key={incidentDetailsItem['source_filekey'] + ':party' + partyIndex}>
                                <TableRow
                                    key={incidentDetailsItem['source_filekey'] + ':incidentParty' + currentPartyNumber}
                                    className={incidentDetailsItem['source_filekey'] === displayedDocument?.['key'] ? "selected" : ""}
                                    sx={{
                                        '&:hover .source-view-icon': {
                                            visibility: 'visible',
                                            opacity: 0.7
                                        }
                                    }}>
                                    <TableCell></TableCell>
                                    <TableCell sx={{ fontWeight: 500 }}> Party {currentPartyNumber++} </TableCell>
                                    <TableCell sx={{ fontWeight: 500 }}> Name </TableCell>
                                    <TableCell colSpan={2}>
                                        <EditableFieldWithWarnings
                                            onWarningClick={(e) => {
                                                e.stopPropagation();
                                                setDisplayedDocument({
                                                    'key': documentKey,
                                                    'page': 1
                                                });
                                            }}
                                            caseInfo={caseInfo}
                                            setCaseInfo={setCaseInfo}
                                            {...buildPartyFieldPaths(documentKey, itemIndex, partyIndex, 'name')}
                                            placeholder="Name not available"
                                            setSnackBarMessage={setSnackBarMessage}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                            <Typography
                                                component="span"
                                                title="Click to view source document"
                                                sx={{
                                                    cursor: 'pointer',
                                                    textDecoration: 'underline',
                                                    fontSize: '0.9rem',
                                                }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setDisplayedDocument({
                                                        'key': incidentDetailsItem['source_filekey'],
                                                        'page': 1
                                                    });
                                                }}
                                            >
                                                {filename}
                                            </Typography>
                                            <IconButton
                                                className="source-view-icon"
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setDisplayedDocument({
                                                        'key': incidentDetailsItem['source_filekey'],
                                                        'page': 1
                                                    });
                                                }}
                                                sx={{
                                                    padding: '2px',
                                                    visibility: 'hidden',
                                                    opacity: 0,
                                                    transition: 'opacity 0.2s, visibility 0.2s',
                                                    '&:hover': {
                                                        opacity: 1,
                                                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                                    }
                                                }}
                                                title="Click to view source document"
                                            >
                                                <VisibilityIcon />
                                            </IconButton>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                                {party.role && <TableRow
                                    key={incidentDetailsItem['source_filekey'] + ':incidentParty' + currentPartyNumber + ':role'}
                                    className={incidentDetailsItem['source_filekey'] === displayedDocument?.['key'] ? "selected" : ""}
                                    sx={{
                                        '&:hover .source-view-icon': {
                                            visibility: 'visible',
                                            opacity: 0.7
                                        }
                                    }}>
                                    <TableCell></TableCell>
                                    <TableCell></TableCell>
                                    <TableCell sx={{ fontWeight: 500 }}> Role </TableCell>
                                    <TableCell colSpan={2}>
                                        <EditableFieldWithWarnings
                                            onWarningClick={(e) => {
                                                e.stopPropagation();
                                                setDisplayedDocument({
                                                    'key': documentKey,
                                                    'page': 1
                                                });
                                            }}
                                            caseInfo={caseInfo}
                                            setCaseInfo={setCaseInfo}
                                            {...buildPartyFieldPaths(documentKey, itemIndex, partyIndex, 'role')}
                                            setSnackBarMessage={setSnackBarMessage}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                            <Typography
                                                component="span"
                                                title="Click to view source document"
                                                sx={{
                                                    cursor: 'pointer',
                                                    textDecoration: 'underline',
                                                    fontSize: '0.9rem',
                                                }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setDisplayedDocument({
                                                        'key': incidentDetailsItem['source_filekey'],
                                                        'page': 1
                                                    });
                                                }}
                                            >
                                                {filename}
                                            </Typography>
                                            <IconButton
                                                className="source-view-icon"
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setDisplayedDocument({
                                                        'key': incidentDetailsItem['source_filekey'],
                                                        'page': 1
                                                    });
                                                }}
                                                sx={{
                                                    padding: '2px',
                                                    visibility: 'hidden',
                                                    opacity: 0,
                                                    transition: 'opacity 0.2s, visibility 0.2s',
                                                    '&:hover': {
                                                        opacity: 1,
                                                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                                    }
                                                }}
                                                title="Click to view source document"
                                            >
                                                <VisibilityIcon />
                                            </IconButton>
                                        </Box>
                                    </TableCell>
                                </TableRow>}
                                {party.statements && <TableRow
                                    key={incidentDetailsItem['source_filekey'] + ':incidentParty' + currentPartyNumber + ':statements'}
                                    className={incidentDetailsItem['source_filekey'] === displayedDocument?.['key'] ? "selected" : ""}
                                    sx={{
                                        '&:hover .source-view-icon': {
                                            visibility: 'visible',
                                            opacity: 0.7
                                        }
                                    }}>
                                    <TableCell></TableCell>
                                    <TableCell></TableCell>
                                    <TableCell sx={{ fontWeight: 500 }}> Statements </TableCell>
                                    <TableCell colSpan={2}>
                                        <EditableFieldWithWarnings
                                            onWarningClick={(e) => {
                                                e.stopPropagation();
                                                setDisplayedDocument({
                                                    'key': documentKey,
                                                    'page': 1
                                                });
                                            }}
                                            caseInfo={caseInfo}
                                            setCaseInfo={setCaseInfo}
                                            {...buildPartyFieldPaths(documentKey, itemIndex, partyIndex, 'statements')}
                                            setSnackBarMessage={setSnackBarMessage}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                            <Typography
                                                component="span"
                                                title="Click to view source document"
                                                sx={{
                                                    cursor: 'pointer',
                                                    textDecoration: 'underline',
                                                    fontSize: '0.9rem',
                                                }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setDisplayedDocument({
                                                        'key': incidentDetailsItem['source_filekey'],
                                                        'page': 1
                                                    });
                                                }}
                                            >
                                                {filename}
                                            </Typography>
                                            <IconButton
                                                className="source-view-icon"
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setDisplayedDocument({
                                                        'key': incidentDetailsItem['source_filekey'],
                                                        'page': 1
                                                    });
                                                }}
                                                sx={{
                                                    padding: '2px',
                                                    visibility: 'hidden',
                                                    opacity: 0,
                                                    transition: 'opacity 0.2s, visibility 0.2s',
                                                    '&:hover': {
                                                        opacity: 1,
                                                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                                    }
                                                }}
                                                title="Click to view source document"
                                            >
                                                <VisibilityIcon />
                                            </IconButton>
                                        </Box>
                                    </TableCell>
                                </TableRow>}
                                {party.action_preceding_incident && party.action_preceding_incident.length > 0 && <TableRow
                                    key={incidentDetailsItem['source_filekey'] + ':incidentParty' + currentPartyNumber + ':action_preceding_incident'}
                                    className={incidentDetailsItem['source_filekey'] === displayedDocument?.['key'] ? "selected" : ""}
                                    sx={{
                                        '&:hover .source-view-icon': {
                                            visibility: 'visible',
                                            opacity: 0.7
                                        }
                                    }}>
                                    <TableCell></TableCell>
                                    <TableCell></TableCell>
                                    <TableCell sx={{ fontWeight: 500 }}> Action Preceding Incident </TableCell>
                                    <TableCell colSpan={2}>
                                        <EditableFieldWithWarnings
                                            onWarningClick={(e) => {
                                                e.stopPropagation();
                                                setDisplayedDocument({
                                                    'key': documentKey,
                                                    'page': 1
                                                });
                                            }}
                                            caseInfo={caseInfo}
                                            setCaseInfo={setCaseInfo}
                                            {...buildPartyFieldPaths(documentKey, itemIndex, partyIndex, 'action_preceding_incident')}
                                            setSnackBarMessage={setSnackBarMessage}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                            <Typography
                                                component="span"
                                                title="Click to view source document"
                                                sx={{
                                                    cursor: 'pointer',
                                                    textDecoration: 'underline',
                                                    fontSize: '0.9rem',
                                                }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setDisplayedDocument({
                                                        'key': incidentDetailsItem['source_filekey'],
                                                        'page': 1
                                                    });
                                                }}
                                            >
                                                {filename}
                                            </Typography>
                                            <IconButton
                                                className="source-view-icon"
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setDisplayedDocument({
                                                        'key': incidentDetailsItem['source_filekey'],
                                                        'page': 1
                                                    });
                                                }}
                                                sx={{
                                                    padding: '2px',
                                                    visibility: 'hidden',
                                                    opacity: 0,
                                                    transition: 'opacity 0.2s, visibility 0.2s',
                                                    '&:hover': {
                                                        opacity: 1,
                                                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                                    }
                                                }}
                                                title="Click to view source document"
                                            >
                                                <VisibilityIcon />
                                            </IconButton>
                                        </Box>
                                    </TableCell>
                                </TableRow>}
                                {party.incident_factors && party.incident_factors.length > 0 && <TableRow
                                    key={incidentDetailsItem['source_filekey'] + ':incidentParty' + currentPartyNumber + ':incident_factors'}
                                    className={incidentDetailsItem['source_filekey'] === displayedDocument?.['key'] ? "selected" : ""}
                                    sx={{
                                        '&:hover .source-view-icon': {
                                            visibility: 'visible',
                                            opacity: 0.7
                                        }
                                    }}>
                                    <TableCell></TableCell>
                                    <TableCell></TableCell>
                                    <TableCell sx={{ fontWeight: 500 }}> Incident Factors </TableCell>
                                    <TableCell colSpan={2}>{party.incident_factors.join(', ')}</TableCell>
                                    <TableCell>
                                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                            <Typography
                                                component="span"
                                                title="Click to view source document"
                                                sx={{
                                                    cursor: 'pointer',
                                                    textDecoration: 'underline',
                                                    fontSize: '0.9rem',
                                                }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setDisplayedDocument({
                                                        'key': incidentDetailsItem['source_filekey'],
                                                        'page': 1
                                                    });
                                                }}
                                            >
                                                {filename}
                                            </Typography>
                                            <IconButton
                                                className="source-view-icon"
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setDisplayedDocument({
                                                        'key': incidentDetailsItem['source_filekey'],
                                                        'page': 1
                                                    });
                                                }}
                                                sx={{
                                                    padding: '2px',
                                                    visibility: 'hidden',
                                                    opacity: 0,
                                                    transition: 'opacity 0.2s, visibility 0.2s',
                                                    '&:hover': {
                                                        opacity: 1,
                                                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                                    }
                                                }}
                                                title="Click to view source document"
                                            >
                                                <VisibilityIcon />
                                            </IconButton>
                                        </Box>
                                    </TableCell>
                                </TableRow>}
                                <TableRow
                                    key={incidentDetailsItem['source_filekey'] + ':incidentParty' + currentPartyNumber + ':violations'}
                                    className={incidentDetailsItem['source_filekey'] === displayedDocument?.['key'] ? "selected" : ""}
                                    sx={{
                                        '&:hover .source-view-icon': {
                                            visibility: 'visible',
                                            opacity: 0.7
                                        }
                                    }}>
                                    <TableCell></TableCell>
                                    <TableCell></TableCell>
                                    <TableCell sx={{ fontWeight: 500 }}> Violations </TableCell>
                                    <TableCell colSpan={2} sx={{ whiteSpace: 'pre-wrap' }}>
                                        <EditableFieldWithWarnings
                                            onWarningClick={(e) => {
                                                e.stopPropagation();
                                                setDisplayedDocument({
                                                    'key': documentKey,
                                                    'page': 1
                                                });
                                            }}
                                            caseInfo={caseInfo}
                                            setCaseInfo={setCaseInfo}
                                            {...buildPartyFieldPaths(documentKey, itemIndex, partyIndex, 'violations')}
                                            placeholder="None"
                                            setSnackBarMessage={setSnackBarMessage}
                                            formatValue={(value) => Array.isArray(value) ? value.join(', ') : (value || 'None')}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                            <Typography
                                                component="span"
                                                title="Click to view source document"
                                                sx={{
                                                    cursor: 'pointer',
                                                    textDecoration: 'underline',
                                                    fontSize: '0.9rem',
                                                }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setDisplayedDocument({
                                                        'key': incidentDetailsItem['source_filekey'],
                                                        'page': 1
                                                    });
                                                }}
                                            >
                                                {filename}
                                            </Typography>
                                            <IconButton
                                                className="source-view-icon"
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setDisplayedDocument({
                                                        'key': incidentDetailsItem['source_filekey'],
                                                        'page': 1
                                                    });
                                                }}
                                                sx={{
                                                    padding: '2px',
                                                    visibility: 'hidden',
                                                    opacity: 0,
                                                    transition: 'opacity 0.2s, visibility 0.2s',
                                                    '&:hover': {
                                                        opacity: 1,
                                                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                                    }
                                                }}
                                                title="Click to view source document"
                                            >
                                                <VisibilityIcon />
                                            </IconButton>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                                {party.vehicle && <React.Fragment key={incidentDetailsItem['source_filekey'] + ':vehicle'}><TableRow
                                    key={incidentDetailsItem['source_filekey'] + ':incidentParty' + currentPartyNumber + ':vehicle'}
                                    className={incidentDetailsItem['source_filekey'] === displayedDocument?.['key'] ? "selected" : ""}
                                    sx={{
                                        '&:hover .source-view-icon': {
                                            visibility: 'visible',
                                            opacity: 0.7
                                        }
                                    }}>
                                    <TableCell></TableCell>
                                    <TableCell></TableCell>
                                    <TableCell sx={{ fontWeight: 500 }}> Vehicle </TableCell>
                                    <TableCell sx={{ fontWeight: 500 }}> Make </TableCell>
                                    <TableCell>
                                        <EditableFieldWithWarnings
                                            onWarningClick={(e) => {
                                                e.stopPropagation();
                                                setDisplayedDocument({
                                                    'key': documentKey,
                                                    'page': 1
                                                });
                                            }}
                                            caseInfo={caseInfo}
                                            setCaseInfo={setCaseInfo}
                                            {...buildVehicleFieldPaths(documentKey, itemIndex, partyIndex, 'make')}
                                            setSnackBarMessage={setSnackBarMessage}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                            <Typography
                                                component="span"
                                                title="Click to view source document"
                                                sx={{
                                                    cursor: 'pointer',
                                                    textDecoration: 'underline',
                                                    fontSize: '0.9rem',
                                                }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setDisplayedDocument({
                                                        'key': incidentDetailsItem['source_filekey'],
                                                        'page': 1
                                                    });
                                                }}
                                            >
                                                {filename}
                                            </Typography>
                                            <IconButton
                                                className="source-view-icon"
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setDisplayedDocument({
                                                        'key': incidentDetailsItem['source_filekey'],
                                                        'page': 1
                                                    });
                                                }}
                                                sx={{
                                                    padding: '2px',
                                                    visibility: 'hidden',
                                                    opacity: 0,
                                                    transition: 'opacity 0.2s, visibility 0.2s',
                                                    '&:hover': {
                                                        opacity: 1,
                                                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                                    }
                                                }}
                                                title="Click to view source document"
                                            >
                                                <VisibilityIcon />
                                            </IconButton>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                                    <TableRow
                                        key={incidentDetailsItem['source_filekey'] + ':incidentParty' + currentPartyNumber + ':vehicle:model'}
                                        className={incidentDetailsItem['source_filekey'] === displayedDocument?.['key'] ? "selected" : ""}
                                        sx={{
                                            '&:hover .source-view-icon': {
                                                visibility: 'visible',
                                                opacity: 0.7
                                            }
                                        }}>
                                        <TableCell></TableCell>
                                        <TableCell></TableCell>
                                        <TableCell></TableCell>
                                        <TableCell sx={{ fontWeight: 500 }}> Model </TableCell>
                                        <TableCell>
                                            <EditableFieldWithWarnings
                                                onWarningClick={(e) => {
                                                    e.stopPropagation();
                                                    setDisplayedDocument({
                                                        'key': documentKey,
                                                        'page': 1
                                                    });
                                                }}
                                                caseInfo={caseInfo}
                                                setCaseInfo={setCaseInfo}
                                                {...buildVehicleFieldPaths(documentKey, itemIndex, partyIndex, 'model')}
                                                setSnackBarMessage={setSnackBarMessage}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                                <Typography
                                                    component="span"
                                                    title="Click to view source document"
                                                    sx={{
                                                        cursor: 'pointer',
                                                        textDecoration: 'underline',
                                                        fontSize: '0.9rem',
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setDisplayedDocument({
                                                            'key': incidentDetailsItem['source_filekey'],
                                                            'page': 1
                                                        });
                                                    }}
                                                >
                                                    {filename}
                                                </Typography>
                                                <IconButton
                                                    className="source-view-icon"
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setDisplayedDocument({
                                                            'key': incidentDetailsItem['source_filekey'],
                                                            'page': 1
                                                        });
                                                    }}
                                                    sx={{
                                                        padding: '2px',
                                                        visibility: 'hidden',
                                                        opacity: 0,
                                                        transition: 'opacity 0.2s, visibility 0.2s',
                                                        '&:hover': {
                                                            opacity: 1,
                                                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                                        }
                                                    }}
                                                    title="Click to view source document"
                                                >
                                                    <VisibilityIcon />
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow
                                        key={incidentDetailsItem['source_filekey'] + ':incidentParty' + currentPartyNumber + ':vehicle:color'}
                                        className={incidentDetailsItem['source_filekey'] === displayedDocument?.['key'] ? "selected" : ""}
                                        sx={{
                                            '&:hover .source-view-icon': {
                                                visibility: 'visible',
                                                opacity: 0.7
                                            }
                                        }}>
                                        <TableCell></TableCell>
                                        <TableCell></TableCell>
                                        <TableCell></TableCell>
                                        <TableCell sx={{ fontWeight: 500 }}> Color </TableCell>
                                        <TableCell>
                                            <EditableFieldWithWarnings
                                                onWarningClick={(e) => {
                                                    e.stopPropagation();
                                                    setDisplayedDocument({
                                                        'key': documentKey,
                                                        'page': 1
                                                    });
                                                }}
                                                caseInfo={caseInfo}
                                                setCaseInfo={setCaseInfo}
                                                {...buildVehicleFieldPaths(documentKey, itemIndex, partyIndex, 'color')}
                                                setSnackBarMessage={setSnackBarMessage}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                                <Typography
                                                    component="span"
                                                    title="Click to view source document"
                                                    sx={{
                                                        cursor: 'pointer',
                                                        textDecoration: 'underline',
                                                        fontSize: '0.9rem',
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setDisplayedDocument({
                                                            'key': incidentDetailsItem['source_filekey'],
                                                            'page': 1
                                                        });
                                                    }}
                                                >
                                                    {filename}
                                                </Typography>
                                                <IconButton
                                                    className="source-view-icon"
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setDisplayedDocument({
                                                            'key': incidentDetailsItem['source_filekey'],
                                                            'page': 1
                                                        });
                                                    }}
                                                    sx={{
                                                        padding: '2px',
                                                        visibility: 'hidden',
                                                        opacity: 0,
                                                        transition: 'opacity 0.2s, visibility 0.2s',
                                                        '&:hover': {
                                                            opacity: 1,
                                                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                                        }
                                                    }}
                                                    title="Click to view source document"
                                                >
                                                    <VisibilityIcon />
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow
                                        key={incidentDetailsItem['source_filekey'] + ':incidentParty' + currentPartyNumber + ':vehicle:year'}
                                        className={incidentDetailsItem['source_filekey'] === displayedDocument?.['key'] ? "selected" : ""}
                                        sx={{
                                            '&:hover .source-view-icon': {
                                                visibility: 'visible',
                                                opacity: 0.7
                                            }
                                        }}>
                                        <TableCell></TableCell>
                                        <TableCell></TableCell>
                                        <TableCell></TableCell>
                                        <TableCell sx={{ fontWeight: 500 }}> Year </TableCell>
                                        <TableCell>
                                            <EditableFieldWithWarnings
                                                onWarningClick={(e) => {
                                                    e.stopPropagation();
                                                    setDisplayedDocument({
                                                        'key': documentKey,
                                                        'page': 1
                                                    });
                                                }}
                                                caseInfo={caseInfo}
                                                setCaseInfo={setCaseInfo}
                                                {...buildVehicleFieldPaths(documentKey, itemIndex, partyIndex, 'year')}
                                                setSnackBarMessage={setSnackBarMessage}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                                <Typography
                                                    component="span"
                                                    title="Click to view source document"
                                                    sx={{
                                                        cursor: 'pointer',
                                                        textDecoration: 'underline',
                                                        fontSize: '0.9rem',
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setDisplayedDocument({
                                                            'key': incidentDetailsItem['source_filekey'],
                                                            'page': 1
                                                        });
                                                    }}
                                                >
                                                    {filename}
                                                </Typography>
                                                <IconButton
                                                    className="source-view-icon"
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setDisplayedDocument({
                                                            'key': incidentDetailsItem['source_filekey'],
                                                            'page': 1
                                                        });
                                                    }}
                                                    sx={{
                                                        padding: '2px',
                                                        visibility: 'hidden',
                                                        opacity: 0,
                                                        transition: 'opacity 0.2s, visibility 0.2s',
                                                        '&:hover': {
                                                            opacity: 1,
                                                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                                        }
                                                    }}
                                                    title="Click to view source document"
                                                >
                                                    <VisibilityIcon />
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow
                                        key={incidentDetailsItem['source_filekey'] + ':incidentParty' + currentPartyNumber + ':vehicle:license_plate'}
                                        className={incidentDetailsItem['source_filekey'] === displayedDocument?.['key'] ? "selected" : ""}
                                        sx={{
                                            '&:hover .source-view-icon': {
                                                visibility: 'visible',
                                                opacity: 0.7
                                            }
                                        }}>
                                        <TableCell></TableCell>
                                        <TableCell></TableCell>
                                        <TableCell></TableCell>
                                        <TableCell sx={{ fontWeight: 500 }}> License Plate </TableCell>
                                        <TableCell>
                                            <EditableFieldWithWarnings
                                                onWarningClick={(e) => {
                                                    e.stopPropagation();
                                                    setDisplayedDocument({
                                                        'key': documentKey,
                                                        'page': 1
                                                    });
                                                }}
                                                caseInfo={caseInfo}
                                                setCaseInfo={setCaseInfo}
                                                {...buildVehicleFieldPaths(documentKey, itemIndex, partyIndex, 'license_plate')}
                                                setSnackBarMessage={setSnackBarMessage}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                                <Typography
                                                    component="span"
                                                    title="Click to view source document"
                                                    sx={{
                                                        cursor: 'pointer',
                                                        textDecoration: 'underline',
                                                        fontSize: '0.9rem',
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setDisplayedDocument({
                                                            'key': incidentDetailsItem['source_filekey'],
                                                            'page': 1
                                                        });
                                                    }}
                                                >
                                                    {filename}
                                                </Typography>
                                                <IconButton
                                                    className="source-view-icon"
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setDisplayedDocument({
                                                            'key': incidentDetailsItem['source_filekey'],
                                                            'page': 1
                                                        });
                                                    }}
                                                    sx={{
                                                        padding: '2px',
                                                        visibility: 'hidden',
                                                        opacity: 0,
                                                        transition: 'opacity 0.2s, visibility 0.2s',
                                                        '&:hover': {
                                                            opacity: 1,
                                                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                                        }
                                                    }}
                                                    title="Click to view source document"
                                                >
                                                    <VisibilityIcon />
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow
                                        key={incidentDetailsItem['source_filekey'] + ':incidentParty' + currentPartyNumber + ':vehicle:damages'}
                                        className={incidentDetailsItem['source_filekey'] === displayedDocument?.['key'] ? "selected" : ""}
                                        sx={{
                                            '&:hover .source-view-icon': {
                                                visibility: 'visible',
                                                opacity: 0.7
                                            }
                                        }}>
                                        <TableCell></TableCell>
                                        <TableCell></TableCell>
                                        <TableCell></TableCell>
                                        <TableCell sx={{ fontWeight: 500 }}> Damages </TableCell>
                                        <TableCell>
                                            <EditableFieldWithWarnings
                                                onWarningClick={(e) => {
                                                    e.stopPropagation();
                                                    setDisplayedDocument({
                                                        'key': documentKey,
                                                        'page': 1
                                                    });
                                                }}
                                                caseInfo={caseInfo}
                                                setCaseInfo={setCaseInfo}
                                                {...buildVehicleFieldPaths(documentKey, itemIndex, partyIndex, 'damages')}
                                                setSnackBarMessage={setSnackBarMessage}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                                <Typography
                                                    component="span"
                                                    title="Click to view source document"
                                                    sx={{
                                                        cursor: 'pointer',
                                                        textDecoration: 'underline',
                                                        fontSize: '0.9rem',
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setDisplayedDocument({
                                                            'key': incidentDetailsItem['source_filekey'],
                                                            'page': 1
                                                        });
                                                    }}
                                                >
                                                    {filename}
                                                </Typography>
                                                <IconButton
                                                    className="source-view-icon"
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setDisplayedDocument({
                                                            'key': incidentDetailsItem['source_filekey'],
                                                            'page': 1
                                                        });
                                                    }}
                                                    sx={{
                                                        padding: '2px',
                                                        visibility: 'hidden',
                                                        opacity: 0,
                                                        transition: 'opacity 0.2s, visibility 0.2s',
                                                        '&:hover': {
                                                            opacity: 1,
                                                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                                        }
                                                    }}
                                                    title="Click to view source document"
                                                >
                                                    <VisibilityIcon />
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow
                                        key={incidentDetailsItem['source_filekey'] + ':incidentParty' + currentPartyNumber + ':vehicle:owned_by_driver'}
                                        className={incidentDetailsItem['source_filekey'] === displayedDocument?.['key'] ? "selected" : ""}
                                        sx={{
                                            '&:hover .source-view-icon': {
                                                visibility: 'visible',
                                                opacity: 0.7
                                            }
                                        }}>
                                        <TableCell></TableCell>
                                        <TableCell></TableCell>
                                        <TableCell></TableCell>
                                        <TableCell sx={{ fontWeight: 500 }}> Owned by Driver? </TableCell>
                                        <TableCell> {party.vehicle?.owned_by_driver === null ? 'Unknown' : (party.vehicle?.owned_by_driver ? 'Yes' : 'No')} </TableCell>
                                        <TableCell>
                                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                                <Typography
                                                    component="span"
                                                    title="Click to view source document"
                                                    sx={{
                                                        cursor: 'pointer',
                                                        textDecoration: 'underline',
                                                        fontSize: '0.9rem',
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setDisplayedDocument({
                                                            'key': incidentDetailsItem['source_filekey'],
                                                            'page': 1
                                                        });
                                                    }}
                                                >
                                                    {filename}
                                                </Typography>
                                                <IconButton
                                                    className="source-view-icon"
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setDisplayedDocument({
                                                            'key': incidentDetailsItem['source_filekey'],
                                                            'page': 1
                                                        });
                                                    }}
                                                    sx={{
                                                        padding: '2px',
                                                        visibility: 'hidden',
                                                        opacity: 0,
                                                        transition: 'opacity 0.2s, visibility 0.2s',
                                                        '&:hover': {
                                                            opacity: 1,
                                                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                                        }
                                                    }}
                                                    title="Click to view source document"
                                                >
                                                    <VisibilityIcon />
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                    </TableRow>

                                    <TableRow
                                        key={incidentDetailsItem['source_filekey'] + ':incidentParty' + currentPartyNumber + ':vehicle:insurance_details:policy_number'}
                                        className={incidentDetailsItem['source_filekey'] === displayedDocument?.['key'] ? "selected" : ""}
                                        sx={{
                                            '&:hover .source-view-icon': {
                                                visibility: 'visible',
                                                opacity: 0.7
                                            }
                                        }}>
                                        <TableCell></TableCell>
                                        <TableCell></TableCell>
                                        <TableCell></TableCell>
                                        <TableCell sx={{ fontWeight: 500 }}> Insurance Policy Number </TableCell>
                                        <TableCell>
                                            <EditableFieldWithWarnings
                                                caseInfo={caseInfo}
                                                setCaseInfo={setCaseInfo}
                                                {...buildInsuranceFieldPaths(documentKey, itemIndex, partyIndex, 'policy_number')}
                                                placeholder="Unknown"
                                                setSnackBarMessage={setSnackBarMessage}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                                <Typography
                                                    component="span"
                                                    title="Click to view source document"
                                                    sx={{
                                                        cursor: 'pointer',
                                                        textDecoration: 'underline',
                                                        fontSize: '0.9rem',
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setDisplayedDocument({
                                                            'key': incidentDetailsItem['source_filekey'],
                                                            'page': 1
                                                        });
                                                    }}
                                                >
                                                    {filename}
                                                </Typography>
                                                <IconButton
                                                    className="source-view-icon"
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setDisplayedDocument({
                                                            'key': incidentDetailsItem['source_filekey'],
                                                            'page': 1
                                                        });
                                                    }}
                                                    sx={{
                                                        padding: '2px',
                                                        visibility: 'hidden',
                                                        opacity: 0,
                                                        transition: 'opacity 0.2s, visibility 0.2s',
                                                        '&:hover': {
                                                            opacity: 1,
                                                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                                        }
                                                    }}
                                                    title="Click to view source document"
                                                >
                                                    <VisibilityIcon />
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow
                                        key={incidentDetailsItem['source_filekey'] + ':incidentParty' + currentPartyNumber + ':vehicle:insurance_details:carrier'}
                                        className={incidentDetailsItem['source_filekey'] === displayedDocument?.['key'] ? "selected" : ""}
                                        sx={{
                                            '&:hover .source-view-icon': {
                                                visibility: 'visible',
                                                opacity: 0.7
                                            }
                                        }}>
                                        <TableCell></TableCell>
                                        <TableCell></TableCell>
                                        <TableCell></TableCell>
                                        <TableCell sx={{ fontWeight: 500 }}> Insurance Carrier </TableCell>
                                        <TableCell>
                                            <EditableFieldWithWarnings
                                                onWarningClick={(e) => {
                                                    e.stopPropagation();
                                                    setDisplayedDocument({
                                                        'key': documentKey,
                                                        'page': 1
                                                    });
                                                }}
                                                caseInfo={caseInfo}
                                                setCaseInfo={setCaseInfo}
                                                {...buildInsuranceFieldPaths(documentKey, itemIndex, partyIndex, 'carrier')}
                                                placeholder="Unknown"
                                                setSnackBarMessage={setSnackBarMessage}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                                <Typography
                                                    component="span"
                                                    title="Click to view source document"
                                                    sx={{
                                                        cursor: 'pointer',
                                                        textDecoration: 'underline',
                                                        fontSize: '0.9rem',
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setDisplayedDocument({
                                                            'key': incidentDetailsItem['source_filekey'],
                                                            'page': 1
                                                        });
                                                    }}
                                                >
                                                    {filename}
                                                </Typography>
                                                <IconButton
                                                    className="source-view-icon"
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setDisplayedDocument({
                                                            'key': incidentDetailsItem['source_filekey'],
                                                            'page': 1
                                                        });
                                                    }}
                                                    sx={{
                                                        padding: '2px',
                                                        visibility: 'hidden',
                                                        opacity: 0,
                                                        transition: 'opacity 0.2s, visibility 0.2s',
                                                        '&:hover': {
                                                            opacity: 1,
                                                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                                        }
                                                    }}
                                                    title="Click to view source document"
                                                >
                                                    <VisibilityIcon />
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                </React.Fragment>} {/* end of party vehicle */}
                            </React.Fragment >)
                    })} {/* end of incidentParties*/}

                    {
                        incidentDetailsItem.witnesses && incidentDetailsItem.witnesses.length > 0 && <TableRow
                            key={incidentDetailsItem['source_filekey'] + ':incidentWitnesses'}
                            className={incidentDetailsItem['source_filekey'] === displayedDocument?.['key'] ? "selected" : ""}
                            sx={{
                                '&:hover .source-view-icon': {
                                    visibility: 'visible',
                                    opacity: 0.7
                                }
                            }}>
                            <TableCell colSpan={5} sx={{ fontWeight: 500 }}> Witnesses </TableCell>
                            <TableCell>
                                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                    <Typography
                                        component="span"
                                        title="Click to view source document"
                                        sx={{
                                            cursor: 'pointer',
                                            textDecoration: 'underline',
                                            fontSize: '0.9rem',
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setDisplayedDocument({
                                                'key': incidentDetailsItem['source_filekey'],
                                                'page': 1
                                            });
                                        }}
                                    >
                                        {filename}
                                    </Typography>
                                    <IconButton
                                        className="source-view-icon"
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setDisplayedDocument({
                                                'key': incidentDetailsItem['source_filekey'],
                                                'page': 1
                                            });
                                        }}
                                        sx={{
                                            padding: '2px',
                                            visibility: 'hidden',
                                            opacity: 0,
                                            transition: 'opacity 0.2s, visibility 0.2s',
                                            '&:hover': {
                                                opacity: 1,
                                                backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                            }
                                        }}
                                        title="Click to view source document"
                                    >
                                        <VisibilityIcon />
                                    </IconButton>
                                </Box>
                            </TableCell>
                        </TableRow>
                    }

                    {
                        incidentDetailsItem.witnesses && incidentDetailsItem.witnesses.map((witness, witnessIndex) => {
                            return (
                                <React.Fragment key={incidentDetailsItem['source_filekey'] + ':witness' + witnessIndex}>
                                    <TableRow
                                        key={incidentDetailsItem['source_filekey'] + ':incidentWitness' + currentWitnessNumber}
                                        className={incidentDetailsItem['source_filekey'] === displayedDocument?.['key'] ? "selected" : ""}
                                        sx={{
                                            '&:hover .source-view-icon': {
                                                visibility: 'visible',
                                                opacity: 0.7
                                            }
                                        }}>
                                        <TableCell></TableCell>
                                        <TableCell sx={{ fontWeight: 500 }}> Witness {currentWitnessNumber++} </TableCell>
                                        <TableCell sx={{ fontWeight: 500 }}> Name </TableCell>
                                        <TableCell>
                                            <EditableFieldWithWarnings
                                                onWarningClick={(e) => {
                                                    e.stopPropagation();
                                                    setDisplayedDocument({
                                                        'key': documentKey,
                                                        'page': 1
                                                    });
                                                }}
                                                caseInfo={caseInfo}
                                                setCaseInfo={setCaseInfo}
                                                {...buildWitnessFieldPaths(documentKey, itemIndex, witnessIndex, 'name')}
                                                placeholder="Name not available"
                                                setSnackBarMessage={setSnackBarMessage}
                                            />
                                        </TableCell>
                                        <TableCell></TableCell>
                                        <TableCell>
                                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                                <Typography
                                                    component="span"
                                                    title="Click to view source document"
                                                    sx={{
                                                        cursor: 'pointer',
                                                        textDecoration: 'underline',
                                                        fontSize: '0.9rem',
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setDisplayedDocument({
                                                            'key': incidentDetailsItem['source_filekey'],
                                                            'page': 1
                                                        });
                                                    }}
                                                >
                                                    {filename}
                                                </Typography>
                                                <IconButton
                                                    className="source-view-icon"
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setDisplayedDocument({
                                                            'key': incidentDetailsItem['source_filekey'],
                                                            'page': 1
                                                        });
                                                    }}
                                                    sx={{
                                                        padding: '2px',
                                                        visibility: 'hidden',
                                                        opacity: 0,
                                                        transition: 'opacity 0.2s, visibility 0.2s',
                                                        '&:hover': {
                                                            opacity: 1,
                                                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                                        }
                                                    }}
                                                    title="Click to view source document"
                                                >
                                                    <VisibilityIcon />
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                    {witness.dob && <TableRow
                                        key={incidentDetailsItem['source_filekey'] + ':incidentWitness' + currentWitnessNumber + ':dob'}
                                        className={incidentDetailsItem['source_filekey'] === displayedDocument?.['key'] ? "selected" : ""}
                                        sx={{
                                            '&:hover .source-view-icon': {
                                                visibility: 'visible',
                                                opacity: 0.7
                                            }
                                        }}>
                                        <TableCell></TableCell>
                                        <TableCell></TableCell>
                                        <TableCell sx={{ fontWeight: 500 }}> Date of Birth </TableCell>
                                        <TableCell>
                                            <EditableFieldWithWarnings
                                                onWarningClick={(e) => {
                                                    e.stopPropagation();
                                                    setDisplayedDocument({
                                                        'key': documentKey,
                                                        'page': 1
                                                    });
                                                }}
                                                caseInfo={caseInfo}
                                                setCaseInfo={setCaseInfo}
                                                {...buildWitnessFieldPaths(documentKey, itemIndex, witnessIndex, 'dob')}
                                                setSnackBarMessage={setSnackBarMessage}
                                            />
                                        </TableCell>
                                        <TableCell></TableCell>
                                        <TableCell>
                                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                                <Typography
                                                    component="span"
                                                    title="Click to view source document"
                                                    sx={{
                                                        cursor: 'pointer',
                                                        textDecoration: 'underline',
                                                        fontSize: '0.9rem',
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setDisplayedDocument({
                                                            'key': incidentDetailsItem['source_filekey'],
                                                            'page': 1
                                                        });
                                                    }}
                                                >
                                                    {filename}
                                                </Typography>
                                                <IconButton
                                                    className="source-view-icon"
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setDisplayedDocument({
                                                            'key': incidentDetailsItem['source_filekey'],
                                                            'page': 1
                                                        });
                                                    }}
                                                    sx={{
                                                        padding: '2px',
                                                        visibility: 'hidden',
                                                        opacity: 0,
                                                        transition: 'opacity 0.2s, visibility 0.2s',
                                                        '&:hover': {
                                                            opacity: 1,
                                                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                                        }
                                                    }}
                                                    title="Click to view source document"
                                                >
                                                    <VisibilityIcon />
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                    </TableRow>}
                                    {witness.statements && <TableRow
                                        key={incidentDetailsItem['source_filekey'] + ':incidentWitness' + currentWitnessNumber + ':statements'}
                                        className={incidentDetailsItem['source_filekey'] === displayedDocument?.['key'] ? "selected" : ""}
                                        sx={{
                                            '&:hover .source-view-icon': {
                                                visibility: 'visible',
                                                opacity: 0.7
                                            }
                                        }}>
                                        <TableCell></TableCell>
                                        <TableCell></TableCell>
                                        <TableCell sx={{ fontWeight: 500 }}> Statements </TableCell>
                                        <TableCell>
                                            <EditableFieldWithWarnings
                                                onWarningClick={(e) => {
                                                    e.stopPropagation();
                                                    setDisplayedDocument({
                                                        'key': documentKey,
                                                        'page': 1
                                                    });
                                                }}
                                                caseInfo={caseInfo}
                                                setCaseInfo={setCaseInfo}
                                                {...buildWitnessFieldPaths(documentKey, itemIndex, witnessIndex, 'statements')}
                                                setSnackBarMessage={setSnackBarMessage}
                                            />
                                        </TableCell>
                                        <TableCell></TableCell>
                                        <TableCell>
                                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                                <Typography
                                                    component="span"
                                                    title="Click to view source document"
                                                    sx={{
                                                        cursor: 'pointer',
                                                        textDecoration: 'underline',
                                                        fontSize: '0.9rem',
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setDisplayedDocument({
                                                            'key': incidentDetailsItem['source_filekey'],
                                                            'page': 1
                                                        });
                                                    }}
                                                >
                                                    {filename}
                                                </Typography>
                                                <IconButton
                                                    className="source-view-icon"
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setDisplayedDocument({
                                                            'key': incidentDetailsItem['source_filekey'],
                                                            'page': 1
                                                        });
                                                    }}
                                                    sx={{
                                                        padding: '2px',
                                                        visibility: 'hidden',
                                                        opacity: 0,
                                                        transition: 'opacity 0.2s, visibility 0.2s',
                                                        '&:hover': {
                                                            opacity: 1,
                                                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                                        }
                                                    }}
                                                    title="Click to view source document"
                                                >
                                                    <VisibilityIcon />
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                    </TableRow>}
                                    {witness.address && <TableRow
                                        key={incidentDetailsItem['source_filekey'] + ':incidentWitness' + currentWitnessNumber + ':address'}
                                        className={incidentDetailsItem['source_filekey'] === displayedDocument?.['key'] ? "selected" : ""}
                                        sx={{
                                            '&:hover .source-view-icon': {
                                                visibility: 'visible',
                                                opacity: 0.7
                                            }
                                        }}>
                                        <TableCell></TableCell>
                                        <TableCell></TableCell>
                                        <TableCell sx={{ fontWeight: 500 }}> Address </TableCell>
                                        <TableCell>
                                            <EditableFieldWithWarnings
                                                onWarningClick={(e) => {
                                                    e.stopPropagation();
                                                    setDisplayedDocument({
                                                        'key': documentKey,
                                                        'page': 1
                                                    });
                                                }}
                                                caseInfo={caseInfo}
                                                setCaseInfo={setCaseInfo}
                                                {...buildWitnessFieldPaths(documentKey, itemIndex, witnessIndex, 'address')}
                                                setSnackBarMessage={setSnackBarMessage}
                                            />
                                        </TableCell>
                                        <TableCell></TableCell>
                                        <TableCell>
                                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                                <Typography
                                                    component="span"
                                                    title="Click to view source document"
                                                    sx={{
                                                        cursor: 'pointer',
                                                        textDecoration: 'underline',
                                                        fontSize: '0.9rem',
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setDisplayedDocument({
                                                            'key': incidentDetailsItem['source_filekey'],
                                                            'page': 1
                                                        });
                                                    }}
                                                >
                                                    {filename}
                                                </Typography>
                                                <IconButton
                                                    className="source-view-icon"
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setDisplayedDocument({
                                                            'key': incidentDetailsItem['source_filekey'],
                                                            'page': 1
                                                        });
                                                    }}
                                                    sx={{
                                                        padding: '2px',
                                                        visibility: 'hidden',
                                                        opacity: 0,
                                                        transition: 'opacity 0.2s, visibility 0.2s',
                                                        '&:hover': {
                                                            opacity: 1,
                                                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                                        }
                                                    }}
                                                    title="Click to view source document"
                                                >
                                                    <VisibilityIcon />
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                    </TableRow>}
                                    {witness.phone && <TableRow
                                        key={incidentDetailsItem['source_filekey'] + ':incidentWitness' + currentWitnessNumber + ':phone'}
                                        className={incidentDetailsItem['source_filekey'] === displayedDocument?.['key'] ? "selected" : ""}
                                        sx={{
                                            '&:hover .source-view-icon': {
                                                visibility: 'visible',
                                                opacity: 0.7
                                            }
                                        }}>
                                        <TableCell></TableCell>
                                        <TableCell></TableCell>
                                        <TableCell sx={{ fontWeight: 500 }}> Phone </TableCell>
                                        <TableCell>
                                            <EditableFieldWithWarnings
                                                onWarningClick={(e) => {
                                                    e.stopPropagation();
                                                    setDisplayedDocument({
                                                        'key': documentKey,
                                                        'page': 1
                                                    });
                                                }}
                                                caseInfo={caseInfo}
                                                setCaseInfo={setCaseInfo}
                                                {...buildWitnessFieldPaths(documentKey, itemIndex, witnessIndex, 'phone')}
                                                setSnackBarMessage={setSnackBarMessage}
                                            />
                                        </TableCell>
                                        <TableCell></TableCell>
                                        <TableCell>
                                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                                <Typography
                                                    component="span"
                                                    title="Click to view source document"
                                                    sx={{
                                                        cursor: 'pointer',
                                                        textDecoration: 'underline',
                                                        fontSize: '0.9rem',
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setDisplayedDocument({
                                                            'key': incidentDetailsItem['source_filekey'],
                                                            'page': 1
                                                        });
                                                    }}
                                                >
                                                    {filename}
                                                </Typography>
                                                <IconButton
                                                    className="source-view-icon"
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setDisplayedDocument({
                                                            'key': incidentDetailsItem['source_filekey'],
                                                            'page': 1
                                                        });
                                                    }}
                                                    sx={{
                                                        padding: '2px',
                                                        visibility: 'hidden',
                                                        opacity: 0,
                                                        transition: 'opacity 0.2s, visibility 0.2s',
                                                        '&:hover': {
                                                            opacity: 1,
                                                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                                        }
                                                    }}
                                                    title="Click to view source document"
                                                >
                                                    <VisibilityIcon />
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                    </TableRow>}
                                </React.Fragment>
                            );
                        })
                    } {/* end of incidentWitnesses */}
                </React.Fragment >
            )
        })

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2
            }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    {isIncidentDetailsExtractionInProgress && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <img
                                src="/loading.gif"
                                alt="loading..."
                                title="Processing..."
                                style={{
                                    marginRight: "8px",
                                    height: "16px",
                                    verticalAlign: "middle"
                                }}
                            />
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'var(--text-primary)',
                                    fontStyle: 'italic',
                                    fontSize: '1.1rem',
                                    fontWeight: 500
                                }}
                            >
                                Incident details are being extracted...
                            </Typography>
                        </Box>
                    )}
                </Box>
                <CopyToClipboardButton nodeRef={tableRef} setSnackBarMessage={setSnackBarMessage} buttonText="Copy Incident Details" />
            </Box>
            <Table
                ref={tableRef}
                sx={{
                    minWidth: 650,
                    maxWidth: '100%', // This is needed to prevent the table from expanding beyond the container
                    tableLayout: 'fixed', // This is needed to prevent the table from expanding beyond the container
                    backgroundColor: 'var(--background-color)',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    border: '1px solid var(--border-color)',
                    boxShadow: 'var(--shadow-light)'
                }}
                aria-label="incident details table"
            >
                <TableHead>
                    <TableRow>
                        <TableCell colSpan={5}>
                            Incident Details
                        </TableCell>
                        <TableCell>Source Document</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {tableBodyRows}
                </TableBody>
            </Table>
        </Box>
    )
}
